const POOL_SIZE = 49;
const PICK_COUNT = 6;
const MAX_UINT32 = 0x100000000;

const secureRandomIndex = (limit) => {
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new RangeError('Limita trebuie să fie un număr întreg pozitiv.');
  }

  const acceptedRange = Math.floor(MAX_UINT32 / limit) * limit;
  const randomValue = new Uint32Array(1);

  do {
    crypto.getRandomValues(randomValue);
  } while (randomValue[0] >= acceptedRange);

  return randomValue[0] % limit;
};

const validateNumbers = (numbers) => (
  Array.isArray(numbers) &&
  numbers.length === PICK_COUNT &&
  new Set(numbers).size === PICK_COUNT &&
  numbers.every((number) => (
    Number.isInteger(number) &&
    number >= 1 &&
    number <= POOL_SIZE
  ))
);

const getMetrics = (numbers) => {
  const sorted = [...numbers].sort((first, second) => first - second);
  const oddCount = sorted.filter((number) => number % 2 !== 0).length;
  const lowCount = sorted.filter((number) => number <= 24).length;
  const sum = sorted.reduce((total, number) => total + number, 0);
  const range = sorted.at(-1) - sorted[0];
  const zones = new Set(
    sorted.map((number) => Math.floor((number - 1) / 10))
  ).size;
  const consecutivePairs = sorted
    .slice(1)
    .filter((number, index) => number - sorted[index] === 1)
    .length;

  return {
    oddCount,
    lowCount,
    sum,
    range,
    zones,
    consecutivePairs
  };
};

export const generateLotoNumbers = () => {
  const pool = Array.from(
    { length: POOL_SIZE },
    (_, index) => index + 1
  );

  for (let index = 0; index < PICK_COUNT; index += 1) {
    const selectedIndex = index + secureRandomIndex(POOL_SIZE - index);
    [pool[index], pool[selectedIndex]] = [
      pool[selectedIndex],
      pool[index]
    ];
  }

  return pool
    .slice(0, PICK_COUNT)
    .sort((first, second) => first - second);
};

export const calculateDistributionScore = (numbers) => {
  if (!validateNumbers(numbers)) return 0;

  const metrics = getMetrics(numbers);
  const parityScore = Math.max(
    0,
    25 - Math.abs(metrics.oddCount - 3) * 7
  );
  const balanceScore = Math.max(
    0,
    20 - Math.abs(metrics.lowCount - 3) * 6
  );
  const rangeScore = Math.min(
    20,
    Math.round((metrics.range / 40) * 20)
  );
  const zoneScore = Math.min(20, metrics.zones * 4);
  const sumScore = Math.max(
    0,
    15 - Math.round(Math.abs(metrics.sum - 150) / 7)
  );
  const penalty = Math.max(0, metrics.consecutivePairs - 1) * 5;

  return Math.max(
    20,
    Math.min(
      95,
      parityScore +
        balanceScore +
        rangeScore +
        zoneScore +
        sumScore -
        penalty
    )
  );
};

export const calculateAlgorithmConfidence = (numbers) => {
  if (!validateNumbers(numbers)) return 0;

  const metrics = getMetrics(numbers);
  const distributionScore = calculateDistributionScore(numbers);
  const parityStability = 100 - Math.abs(metrics.oddCount - 3) * 18;
  const rangeStability = Math.min(100, Math.round(metrics.range * 2.5));
  const zoneStability = metrics.zones * 20;
  const sumStability = Math.max(
    35,
    100 - Math.round(Math.abs(metrics.sum - 150) * 1.1)
  );

  return Math.max(
    25,
    Math.min(
      95,
      Math.round(
        distributionScore * 0.4 +
        parityStability * 0.2 +
        rangeStability * 0.15 +
        zoneStability * 0.15 +
        sumStability * 0.1
      )
    )
  );
};

export const createVariant = (id) => {
  const numbers = generateLotoNumbers();

  return {
    id,
    numbers,
    distributionScore: calculateDistributionScore(numbers),
    confidenceScore: calculateAlgorithmConfidence(numbers),
    createdAt: new Date().toISOString()
  };
};

export const generateVariantBatch = (count, startId = 1) => (
  Array.from(
    { length: count },
    (_, index) => createVariant(startId + index)
  )
);

export const rankVariants = (variants) => (
  [...variants].sort((first, second) => (
    second.confidenceScore - first.confidenceScore ||
    second.distributionScore - first.distributionScore
  ))
);

export const calculateConfidence = calculateDistributionScore;